import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Search, ShoppingBag, Store, ShoppingCart, Truck, ShieldCheck, Users, Headphones, LayoutDashboard, Phone, Mail } from "lucide-react";
import client from "@/lib/api";
import useMeta from "@/lib/useMeta";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import ProductCard from "@/components/ProductCard";
import HeroCarousel from "@/components/HeroCarousel";

export default function Home() {
  const nav = useNavigate();
  const meta = useMeta();
  const { count } = useCart();
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      client
        .get("/products", { params: { cat, q: q || undefined } })
        .then((r) => setProducts(r.data))
        .finally(() => setLoading(false));
    }, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [cat, q]);

  const goSell = () => nav("/merchant");

  return (
    <div dir="rtl">
      {/* Header */}
      <div className="sticky top-0 z-20 px-4 pt-4 pb-3" style={{ background: "linear-gradient(180deg,#12203A,#1b2c4d)" }}>
        <div className="flex items-center justify-between">
          <div className="leading-tight">
            <div className="font-head font-black text-[24px] text-white tracking-tight">كزيوني</div>
            <div className="text-[11.5px] mt-0.5" style={{ color: "#C7B08A" }}>من تاجر لتاجر · سوق الجملة الليبي</div>
          </div>
          <div className="flex gap-2">
            {user && (
              <button data-testid="home-dashboard-btn" onClick={() => nav("/dashboard")} className="k-tap w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "rgba(255,255,255,.1)", color: "#fff" }}>
                <LayoutDashboard size={18} />
              </button>
            )}
            <button data-testid="notifications-btn" onClick={() => nav("/notifications")} className="k-tap w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "rgba(255,255,255,.1)", color: "#fff" }}>
              <Bell size={18} />
            </button>
            <button data-testid="header-cart-btn" onClick={() => nav("/cart")} className="k-tap w-10 h-10 rounded-full flex items-center justify-center relative" style={{ background: "rgba(255,255,255,.1)", color: "#fff" }}>
              <ShoppingCart size={18} />
              {count > 0 && <span data-testid="header-cart-badge" className="font-num absolute -top-1 -right-1 text-[10px] font-bold flex items-center justify-center text-white" style={{ minWidth: 17, height: 17, borderRadius: 9, background: "var(--k-gold)", padding: "0 4px" }}>{count}</span>}
            </button>
          </div>
        </div>

        {/* search */}
        <div className="mt-3 flex items-center gap-2 rounded-2xl px-3.5 py-3" style={{ background: "rgba(255,255,255,.12)" }}>
          <Search size={18} style={{ color: "#C7B08A" }} />
          <input
            data-testid="home-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="ابحث عن منتج، تاجر، مدينة…"
            className="bg-transparent border-0 outline-none w-full text-[14px] text-white placeholder:text-[#9fb0cc]"
          />
        </div>
      </div>

      {/* Hero banner carousel (admin-managed ads) */}
      <HeroCarousel />

      {/* Trust badges */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 px-4 pt-4">
        {[[Users, "تجار موثوقون"], [ShieldCheck, "دفع آمن"], [Truck, "توصيل سريع"], [Headphones, "دعم فني"]].map(([Icon, label], i) => (
          <div key={i} className="rounded-2xl p-3.5 flex flex-col items-center gap-1.5 text-center" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
            <span className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "var(--k-gold-tint)", color: "var(--k-gold-deep)" }}><Icon size={18} /></span>
            <span className="text-[12px] font-bold">{label}</span>
          </div>
        ))}
      </div>

      {/* Buy / Sell */}
      <div className="flex gap-2.5 px-4 pt-4">
        <button data-testid="buy-choice" onClick={() => nav("/browse")} className="k-tap flex-1 rounded-3xl p-4 text-right flex flex-col gap-6 min-h-[112px]" style={{ background: "var(--k-ink)", color: "#fff" }}>
          <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "rgba(217,119,6,.22)", color: "var(--k-gold)" }}><ShoppingBag size={19} /></span>
          <span><strong className="font-head text-[15px]">أريد أن أشتري</strong><small className="block font-normal text-[11.5px] opacity-75 mt-0.5">تصفّح آلاف عروض الجملة</small></span>
        </button>
        <button data-testid="sell-choice" onClick={goSell} className="k-tap flex-1 rounded-3xl p-4 text-right flex flex-col gap-6 min-h-[112px]" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
          <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "var(--k-gold-tint)", color: "var(--k-gold-deep)" }}><Store size={19} /></span>
          <span><strong className="font-head text-[15px]">أريد أن أبيع</strong><small className="block font-normal text-[11.5px] mt-0.5" style={{ color: "var(--k-ink-soft)" }}>اعرض مخزونك على تجار ليبيا</small></span>
        </button>
      </div>

      {/* chips */}
      <div className="flex gap-2 px-4 pt-4 pb-1 overflow-x-auto no-scrollbar">
        {[{ id: "all", name: "الكل" }, ...meta.categories].map((c) => {
          const active = cat === c.id;
          return (
            <button key={c.id} data-testid={`cat-chip-${c.id}`} onClick={() => setCat(c.id)} className="k-tap shrink-0 px-3.5 py-2 rounded-full text-[12.5px] font-semibold whitespace-nowrap" style={{ background: active ? "var(--k-ink)" : "var(--k-surface)", color: active ? "#fff" : "var(--k-ink-soft)", border: `1px solid ${active ? "var(--k-ink)" : "var(--k-line)"}` }}>
              {c.name}
            </button>
          );
        })}
      </div>

      {/* section */}
      <div className="flex items-baseline justify-between px-4 pt-4 pb-2.5">
        <h2 className="font-head text-[16px] font-bold">عروض الجملة اليوم</h2>
        <span data-testid="result-count" className="text-[12px]" style={{ color: "var(--k-ink-soft)" }}>{products.length} منتج</span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2.5 px-4 md:px-6 pb-6">
        {loading ? (
          Array.from({ length: 5 }).map((_, i) => <div key={i} className="rounded-2xl animate-pulse" style={{ height: 170, background: "var(--k-surface)", border: "1px solid var(--k-line)" }} />)
        ) : products.length ? (
          products.map((p) => <ProductCard key={p.id} p={p} />)
        ) : (
          <div className="col-span-full text-center py-14" style={{ color: "var(--k-ink-soft)" }}>
            <Search size={38} className="mx-auto mb-3 opacity-50" />
            <p className="text-[13.5px]">لا توجد نتائج مطابقة</p>
          </div>
        )}
      </div>

      {/* Contact us card */}
      <div className="rounded-2xl mx-4 mt-4 p-5" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
        <h2 className="font-head text-[16px] font-bold mb-1">اتصل بنا</h2>
        <p className="text-[12px] mb-3" style={{ color: "var(--k-ink-soft)" }}>فريقنا جاهز لمساعدتك في أي وقت</p>
        <a href="tel:0913259296" data-testid="home-contact-phone" className="k-tap flex items-center justify-between gap-2 rounded-xl px-3 py-3 mb-2.5" style={{ background: "var(--k-paper)", border: "1px solid var(--k-line)", color: "var(--k-ink)" }}>
          <span className="flex items-center gap-2 font-num"><Phone size={16} style={{ color: "var(--k-gold-deep)" }} /> 0913259296</span>
          <span className="text-[11px]" style={{ color: "var(--k-ink-soft)" }}>رقم موحّد لكل الأقسام</span>
        </a>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
          <a href="mailto:info@kazuoni.com" data-testid="home-contact-info" className="k-tap flex items-center gap-2 rounded-xl px-3 py-3 text-[13px]" style={{ background: "var(--k-paper)", border: "1px solid var(--k-line)", color: "var(--k-ink)" }}><Mail size={16} style={{ color: "var(--k-gold-deep)" }} /> info@kazuoni.com</a>
          <a href="mailto:sales@kazuoni.com" data-testid="home-contact-sales" className="k-tap flex items-center gap-2 rounded-xl px-3 py-3 text-[13px]" style={{ background: "var(--k-paper)", border: "1px solid var(--k-line)", color: "var(--k-ink)" }}><Mail size={16} style={{ color: "var(--k-gold-deep)" }} /> sales@kazuoni.com</a>
        </div>
      </div>

      {/* Footer */}
      <footer className="mt-6 px-4 py-8" style={{ background: "var(--k-ink)", color: "#fff" }}>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 max-w-[1180px] mx-auto">
          <div>
            <div className="font-head font-black text-[20px]">كزيوني</div>
            <p className="text-[12px] mt-1" style={{ color: "#9fb0cc" }}>سوق الجملة الليبي من تاجر لتاجر</p>
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-2 text-[12.5px]" style={{ color: "#C7B08A" }}>
            <button onClick={() => nav("/browse")} className="k-tap bg-transparent border-0">تصفّح المنتجات</button>
            <button onClick={() => nav("/merchant")} className="k-tap bg-transparent border-0">سجّل كتاجر</button>
            <button onClick={() => nav("/account")} className="k-tap bg-transparent border-0">حسابي</button>
          </div>
        </div>
        <div className="flex flex-wrap justify-center gap-x-5 gap-y-1 text-[12px] mt-5" style={{ color: "#C7B08A" }}>
          <a href="tel:0913259296" className="font-num">📞 0913259296</a>
          <a href="mailto:info@kazuoni.com">info@kazuoni.com</a>
          <a href="mailto:sales@kazuoni.com">sales@kazuoni.com</a>
        </div>
        <p className="text-[11px] text-center mt-4" style={{ color: "#6b7d9c" }}>© 2026 كزيوني — جميع الحقوق محفوظة</p>
      </footer>
    </div>
  );
}
